import { currentClinic } from 'boot/currentClinic';
import { auth, db } from 'boot/firebase';
import { network } from 'boot/network';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { chunk, compact, concat, flatten, get, has, head, isArray, isNil, isObject, keys, map, omit } from 'lodash';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { logEvent } from 'src/utils/LogEvent';

/**
 * @typedef {'creating' | 'reading' | 'updating' | 'deleting' | 'executing' | 'getting' |
 * 'execute-transaction'} DbOperation
 */

/** @typedef {import('firebase/firestore').Query} Query */
/** @typedef {import('firebase/firestore').CollectionReference} CollectionReference */
/** @typedef {import('firebase/firestore').WhereFilterOp} WhereFilterOp */

/** @typedef {Array<string, WhereFilterOp, Any>} Constraint */

/** @typedef {{
 * constraints?: Array<Constraint> | Array<any> | null,
 * queryOrderBy?: string | null,
 * orderDirection?: 'asc' | 'desc' | null,
 * queryLimit?: number| null,
 * offset?: number| null,
 * forceIndexedDB?: boolean
 }} ReadAllArgs */

/** @typedef {{
 * data: Object,
 * id?: string | null,
 * merge?: boolean,
 * overwriteCreateTimestamp?: Timestamp,
 * overwriteUpdateTimestamp?: Timestamp,
 }} CreateArgs */

/** @typedef {{
 *  read: (collDb: GenericDB, readArgs: Object) => Promise<Object>,
 *  readAll: (collDb: GenericDB, readAllArgs?: Object) => Promise<Object>,
 *  create: (collDb: GenericDB, createData: Object) => Promise<Object>,
 *  update: (collDb: GenericDB, updateData: Object) => Promise<string>,
 *  set: (collDb: GenericDB, setData: Object) => Promise<void>,
 *  delete: (collDb: GenericDB, id: string) => Promise<void>,
 * }} TransactionMethods
 *
 * TODO For now transaction methods don't return anything in offline mode
 */

const isOfflineModeAvailable = () => network.offlineMode || network.offlineModeAvailable;

export default class GenericDB {
  constructor(collectionPath, errorCollectionPath) {
    this.collectionPath = collectionPath;
    this.errorCollectionPath = collectionPath || errorCollectionPath || 'unknown';
  }

  /**
   * Get reference to collection
   * Can be overwritten by child classes to reference to sub-collections
   */
  getCollectionRef() {
    return collection(db, this.collectionPath);
  }

  /**
   * A new Firestore auto-id, generated LOCALLY — `doc()` with no path computes one client-side, so
   * this costs no round-trip and works offline.
   *
   * For documents whose id has to exist before the write: anything staged in a unit of work, or
   * anything referenced by a sibling document written in the same transaction. Prefer this over a
   * UUID so ids look like every other id in the database; both are random, so both spread writes
   * evenly across the key range, but only one of them reads as native.
   *
   * @returns {string}
   */
  newId() {
    return doc(this.getCollectionRef()).id;
  }

  /**
   * @param {string} id - id of the document in collection
   * @param {string} subcollection - name of the document subcollection
   */
  getSubcollectionRef(id, subcollection) {
    const collectionRef = this.getCollectionRef();
    return collection(collectionRef, id, subcollection);
  }

  /** @param {string} docId */
  async getDocSnapshot(docId) {
    if (network.offlineMode) {
      const idxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return idxDb.getDocSnapshot(docId);
    }

    const docRef = this.getDocRef(docId);
    return getDoc(docRef);
  }

  /**
   * @param {string} docId
   * @return { DocumentReference<DocumentData> | IdxDbRef}
   */
  getDocRef(docId) {
    if (network.offlineMode) {
      const genericIndexedDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIndexedDb.getDocRef(docId);
    }

    const collectionRef = this.getCollectionRef();
    return doc(collectionRef, docId);
  }

  /**
   * Build a query with provided settings. **Use no more than 30 constraints!**
   *
   * @param {Array<Array<string>> | null} [constraints]
   * @param {string | null} [queryOrderBy]
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   * @param {number | null} [offset]
   * @return {Query<DocumentData> | IdxDbRef}
   *
   * @throws {Error} if there were provided more than 30 constraints
   */
  getQuery(
    constraints,
    queryOrderBy,
    orderDirection,
    queryLimit,
    offset,
  ) {
    if (network.offlineMode) {
      const genericIndexedDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIndexedDb.getQueryRef(constraints, queryOrderBy, orderDirection, queryLimit, offset);
    }

    let queryBuilder = this.getCollectionRef();

    if (constraints && isArray(constraints) && constraints.length >= 1) {
      const tooBigConstraints = constraints
        .filter((w) => w[2] && isArray(w[2]) && w[2].length > 30);

      if (tooBigConstraints.length) {
        throw new Error('Cannot get query for more than 30 constraints');
      }

      const whereConstraints = map(constraints, (constraint) => where(...constraint));
      queryBuilder = query(queryBuilder, ...whereConstraints);
    }

    return this.#manageQuerySettings(queryBuilder, queryOrderBy, orderDirection, queryLimit, offset);
  }

  /**
   * @param {Query | CollectionReference} initQuery
   * @param {string | null} [queryOrderBy]
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   * @param {number | null} [offset]
   */
  // eslint-disable-next-line class-methods-use-this
  #manageQuerySettings(
    initQuery,
    queryOrderBy,
    orderDirection,
    queryLimit,
    offset,
  ) {
    let queryBuilder = initQuery;

    if (queryOrderBy) {
      queryBuilder = query(queryBuilder, orderBy(queryOrderBy, orderDirection || 'asc'));
    }

    if (offset) {
      queryBuilder = query(queryBuilder, startAfter(offset));
    }

    if (queryLimit) {
      queryBuilder = query(queryBuilder, limit(queryLimit));
    }

    return queryBuilder;
  }

  /**
   * Create a document in the collection
   *
   * @param {Object} data
   * @param {string | null} [id]
   * @param {boolean} [merge] Merge data into existing data
   * @param {Timestamp} [overwriteCreateTimestamp] Overwrite create timestamp
   * @param {Timestamp} [overwriteUpdateTimestamp] Overwrite update timestamp
   */
  async create(
    data,
    id = null,
    merge = false,
    overwriteCreateTimestamp = null,
    overwriteUpdateTimestamp = null,
  ) {
    if (network.offlineMode) {
      return this.#createDataInIdxDb(data, id, merge, overwriteCreateTimestamp, overwriteUpdateTimestamp);
    }

    const collectionRef = this.getCollectionRef();
    const timestamp = serverTimestamp();
    const dataToCreate = {
      ...data,
      createTimestamp: overwriteCreateTimestamp || timestamp,
      updateTimestamp: overwriteUpdateTimestamp || timestamp,
      updateBy: get(auth, ['currentUser', 'uid']) || null,
    };
    // The id field is reserved and should not be included
    delete dataToCreate.id;

    let docId = null;

    try {
      if (isNil(id)) {
        const docRef = await addDoc(collectionRef, dataToCreate);
        docId = docRef.id;
      } else {
        const docRef = doc(collectionRef, id);
        await setDoc(docRef, dataToCreate, { merge });
        docId = id;
      }
    } catch (err) {
      this.#handleDbError(err.message, 'creating', {
        dataToCreate,
        id,
        merge,
        collectionPath: this.errorCollectionPath,
      });
    }

    try {
      await this.#createDataInIdxDb(data, docId, merge, overwriteCreateTimestamp, overwriteUpdateTimestamp);
    } catch (err) {
      // should be empty
    }

    return {
      ...data,
      id: docId,
      createTimestamp: overwriteCreateTimestamp instanceof Timestamp ? overwriteCreateTimestamp.toDate() : new Date(),
      updateTimestamp: overwriteUpdateTimestamp instanceof Timestamp ? overwriteUpdateTimestamp.toDate() : new Date(),
      updateBy: get(auth, ['currentUser', 'uid']) || null,
    };
  }

  async #createDataInIdxDb(data, id, merge, overwriteCreateTimestamp, overwriteUpdateTimestamp) {
    if (!isOfflineModeAvailable()) {
      return;
    }

    const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
    return genericIdxDb.create(data, id, merge, overwriteCreateTimestamp, overwriteUpdateTimestamp);
  }

  /**
   * Set specific data on a document in the collection
   * @param {Object} data Data to set
   * @param {string} id Document id to set data on
   * @param {boolean} [forceIndexedDB] - force to set data to indexedDB
   * @return Promise<void>
   */
  async set(data, id = null, forceIndexedDB = false) {
    if (!id) {
      throw new Error(`Document id is missing (${this.errorCollectionPath} collection)`);
    }

    if (network.offlineMode || forceIndexedDB === true) {
      return this.#setDataToIdxDB(data, id);
    }

    try {
      const docRef = this.getDocRef(id);
      const docData = {
        ...data,
        updateTimestamp: serverTimestamp(),
        updateBy: get(auth, ['currentUser', 'uid']) || null,
      };

      await setDoc(docRef, docData, { merge: true });
    } catch (err) {
      this.#handleDbError(err.message, 'updating', {
        data,
        id,
        collectionPath: this.errorCollectionPath,
      });
      return;
    }

    try {
      await this.#setDataToIdxDB(data, id);
    } catch (err) {
      // We don't need to throw IDB error after saving data to the Firestore, even if there is one
    }
  }

  #setDataToIdxDB(data, id) {
    if (!isOfflineModeAvailable()) {
      return;
    }

    const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
    return genericIdxDb.set(data, id);
  }

  /**
   * Read a document in the collection
   * @param {string} id
   * @param {boolean} [forceIndexedDB]
   */
  async read(id, { forceIndexedDB } = {}) {
    if (network.offlineMode || forceIndexedDB) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIdxDb.read(id);
    }

    let result = null;

    try {
      result = await this.getDocSnapshot(id);
    } catch (err) {
      this.#handleDbError(err.message, 'reading', {
        documentId: id,
        collectionPath: this.errorCollectionPath,
      });
    }

    const data = result && result.exists() ? result.data() : null;

    if (isNil(data)) {
      return null;
    }

    return { id, ...data };
  }

  /**
   * Check if a document exists in the collection
   * @param {string} id
   * @param {boolean} [forceIndexedDB]
   */
  async checkIfExists(id, { forceIndexedDB } = {}) {
    if (network.offlineMode || forceIndexedDB) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return !!genericIdxDb.read(id);
    }

    let result = null;

    try {
      result = await this.getDocSnapshot(id);
    } catch (err) {
      this.#handleDbError(err.message, 'reading', {
        documentId: id,
        collectionPath: this.errorCollectionPath,
      });
    }

    return result && result.exists();
  }

  /**
   * Read all documents in the collection following constraints
   *
   * @param {Array<Constraint> | Array<any> | null} [constraints]
   * @param {string | null} [queryOrderBy]
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   * @param {number| null} [offset]
   * @param {Object} [options]
   * @param {boolean} [options.forceIndexedDB]
   * @return {Promise<Array<{id: string} & Object>>}
   */
  async readAll(
    constraints = null,
    queryOrderBy = null,
    orderDirection = null,
    queryLimit = null,
    offset = null,
    { forceIndexedDB = false } = {},
  ) {
    if (network.offlineMode || forceIndexedDB === true) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIdxDb.readAll(constraints, queryOrderBy, orderDirection, queryLimit, offset);
    }

    let queryBuilder = this.getCollectionRef();

    if (constraints && isArray(constraints) && constraints.length >= 1) {
      const whereConstraints = await this.#handleConstraints(constraints);
      if (this.#isDocData(head(whereConstraints))) {
        return whereConstraints;
      }

      queryBuilder = query(queryBuilder, ...whereConstraints);
    }

    queryBuilder = this.#manageQuerySettings(queryBuilder, queryOrderBy, orderDirection, queryLimit, offset);

    try {
      const docsRef = await getDocs(queryBuilder);
      return this.#formatDocsRef(docsRef);
    } catch (err) {
      this.#handleDbError(err.message, 'executing', {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
        offset,
        collectionPath: this.errorCollectionPath,
      });
    }
  }

  /**
   * Reads one page of a collection, cursoring by the raw document snapshot rather than a field
   * value. This needs no `orderBy` and therefore no new composite index — the query keeps the same
   * shape (just the constraints) that already runs today, only bounded by `queryLimit` and resumed
   * from `afterDoc`. Meant for collections too large to read in one `readAll()` without risking a
   * Firestore quota trip (e.g. `integrationCache` on a large clinic).
   *
   * Pagination does not apply in two cases, both returning the entire matching set in one call with
   * `lastDoc: null`: offline mode / `forceIndexedDB` (the IndexedDB mirror has no server round-trip
   * to page), and a constraint array split into multiple `in` queries by `#handleConstraints` (there
   * is no single query left to cursor through). Passing `queryLimit` in either case is silently
   * ignored — check the caller can tolerate an unbounded read before relying on this for a
   * collection where that matters.
   *
   * @param {Array<Constraint> | null} [constraints]
   * @param {number | null} [queryLimit]
   * @param {import('firebase/firestore').QueryDocumentSnapshot | null} [afterDoc]
   * @param {Object} [options]
   * @param {boolean} [options.forceIndexedDB]
   * @return {Promise<{
   *   items: Array<{id: string} & Object>,
   *   lastDoc: import('firebase/firestore').QueryDocumentSnapshot | null,
   * }>}
   */
  async readPage(constraints = null, queryLimit = null, afterDoc = null, { forceIndexedDB = false } = {}) {
    if (network.offlineMode || forceIndexedDB === true) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      // IndexedDB reads are local, so the whole matching set comes back in one call; there is no
      // further page to cursor into.
      const items = await genericIdxDb.readAll(constraints);
      return { items, lastDoc: null };
    }

    let queryBuilder = this.getCollectionRef();

    if (constraints && isArray(constraints) && constraints.length >= 1) {
      const whereConstraints = await this.#handleConstraints(constraints);
      if (this.#isDocData(head(whereConstraints))) {
        return { items: whereConstraints, lastDoc: null };
      }

      queryBuilder = query(queryBuilder, ...whereConstraints);
    }

    if (afterDoc) {
      queryBuilder = query(queryBuilder, startAfter(afterDoc));
    }

    if (queryLimit) {
      queryBuilder = query(queryBuilder, limit(queryLimit));
    }

    try {
      const docsRef = await getDocs(queryBuilder);
      return {
        items: this.#formatDocsRef(docsRef),
        lastDoc: docsRef.docs.length ? docsRef.docs[docsRef.docs.length - 1] : null,
      };
    } catch (err) {
      this.#handleDbError(err.message, 'executing', {
        constraints,
        limit: queryLimit,
        collectionPath: this.errorCollectionPath,
      });
    }
  }

  /**
   * Firebase allows "in" - queries of up to 30 elements arrays only.
   * Filter out queries for arrays with more than 30 elements and see and split those queries into multiple reads.
   *
   * @param {Array<Constraint>} constraints
   * @return {Promise<QueryFieldFilterConstraint[] | Object[]>}
   */
  async #handleConstraints(constraints, transaction = null) {
    const okConstraints = constraints
      .filter((w) => !w[2] || !isArray(w[2]) || w[2].length <= 30);
    const tooBigConstraints = constraints
      .filter((w) => w[2] && isArray(w[2]) && w[2].length > 30);

    if (tooBigConstraints.length > 1) {
      throw new Error('Cannot query for multiple long arrays');
    }

    if (tooBigConstraints.length === 1) {
      // Split reads into chunks of 30
      const tooBigConstraint = tooBigConstraints.pop();
      const tooBigConstraintItems = tooBigConstraint[2];
      const readPromises = flatten(chunk(tooBigConstraintItems, 30).map((queryChunk) => {
        const chunkConstraints = concat(
          okConstraints,
          [[tooBigConstraint[0], tooBigConstraint[1], queryChunk]],
        );

        if (transaction) {
          return this.#readAllWithTransaction(transaction, { constraints: chunkConstraints });
        }

        // @Todo Ordering does currently not work when running +30 element queries
        return this.readAll(chunkConstraints);
      }));

      return flatten(await Promise.all(readPromises));
    }

    return map(constraints, (constraint) => where(...constraint));
  }

  /**
   * Check if subject is DB document
   * @param {any} subject
   * @return {subject is {id: string}}
   */
  // eslint-disable-next-line class-methods-use-this
  #isDocData(subject) {
    return isObject(subject) && has(subject, 'id');
  }

  /**
   * @param {QuerySnapshot<DocumentData>} docsRef
   * @return {Array<{id: string} & DocumentData>}
   */
  // eslint-disable-next-line class-methods-use-this
  #formatDocsRef(docsRef) {
    return docsRef.docs.map((ref) => {
      return { id: ref.id, ...ref.data() };
    });
  }

  /**
   * Read multiple documents in the collection by id.
   * Will exclude non-existant documents in the collection
   * @param ids
   */
  async readMultiple(ids) {
    const readActions = ids.map((id) => this.read(id));
    return compact(await Promise.all(readActions));
  }

  /**
   * Update a document in the collection
   * @param {{id: string} & Object} data
   * @param {boolean} [forceIndexedDB]
   */
  async update(data, forceIndexedDB = false) {
    if (network.offlineMode || forceIndexedDB === true) {
      return this.#updateDataInIdxDB(data);
    }

    const { id } = data;

    try {
      const docRef = this.getDocRef(id);
      await updateDoc(docRef, {
        ...omit(data, 'id'),
        updateTimestamp: serverTimestamp(),
        updateBy: get(auth, ['currentUser', 'uid']) || null,
      });
    } catch (err) {
      this.#handleDbError(err.message, 'updating', {
        data,
        id,
        collectionPath: this.errorCollectionPath,
      });
    }

    try {
      await this.#updateDataInIdxDB(data);
    } catch (err) {
      // We don't need to throw IDB error after saving data to the Firestore, even if there is one
    }

    return id;
  }

  async #updateDataInIdxDB(data) {
    if (!isOfflineModeAvailable()) {
      return;
    }

    // todo: optimize this process by setting GenericIndexedDb instance as a class property and reusing it
    //  to avoid multiple instances creation.
    const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
    return genericIdxDb.update(data);
  }

  /**
   * Delete a document in the collection
   * @param id
   */
  async delete(id) {
    if (network.offlineMode) {
      return this.#deleteDataInIdxDB(id);
    }

    try {
      const docRef = this.getDocRef(id);
      await deleteDoc(docRef);
    } catch (err) {
      this.#handleDbError(err.message, 'deleting', {
        documentId: id,
        collectionPath: this.errorCollectionPath,
      });
    }

    try {
      const deletedDataCollectionRef = collection(db, 'deletedData');
      const expiresAt = Timestamp.fromMillis(Date.now() + (14 * 24 * 60 * 60 * 1000));
      await addDoc(deletedDataCollectionRef, {
        id,
        collectionName: this.getCollectionRef().path,
        clinic: currentClinic.id,
        deleteTimestamp: serverTimestamp(),
        expiresAt,
      });
    } catch (err) {
      // We don't need to throw IDB error, even if there is one
    }

    try {
      await this.#deleteDataInIdxDB(id);
    } catch (err) {
      // We don't need to throw IDB error, even if there is one
    }
  }

  async #deleteDataInIdxDB(id) {
    if (!isOfflineModeAvailable()) {
      return;
    }

    const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
    return genericIdxDb.delete(id);
  }

  // eslint-disable-next-line class-methods-use-this
  async #deleteSubCollectionDataInIdxDB(subCollectionName, id) {
    if (!isOfflineModeAvailable()) {
      return;
    }

    const genericIdxDb = new GenericIndexedDb(subCollectionName);
    return genericIdxDb.delete(id);
  }

  /**
   * Delete all documents in a subcollection - should be used very carefully!
   * @param {string} id - id of the document in collection
   * @param {string} subcollection - name of the document subcollection
   */
  async deleteSubcollection(id, subcollection) {
    const subcollectionRef = this.getSubcollectionRef(id, subcollection);
    // getting all documents of the subcollection
    const docs = await getDocs(subcollectionRef);

    const indexedDBData = [];

    const deleteActions = docs.docs.map((docData) => {
      const docRef = doc(subcollectionRef, docData.id);
      indexedDBData.push(this.#deleteSubCollectionDataInIdxDB(subcollectionRef.path, docData.id));
      return deleteDoc(docRef);
    });

    await Promise.all(deleteActions);
    await Promise.all(indexedDBData);
  } // deleteSubcollection()

  /**
   * Delete all documents matching a query - should be used very carefully!
   * @param {Array<Array<string>>} queryWhere
   */
  async deleteWhere(queryWhere) {
    if (queryWhere.length === 0) {
      // Disallow DELETE ALL to (try to) prevent oops'es
      return;
    }

    this.readAll(queryWhere).then((docs) => {
      docs.forEach((docData) => {
        deleteDoc(this.getDocRef(docData.id));
      });
    });
  } // deleteWhere()

  /**
   * Convert all object Timestamp properties to date
   * @param obj
   */
  convertObjectTimestampPropertiesToDate(obj) {
    const newObj = {};
    keys(obj)
      .filter((prop) => obj[prop] instanceof Object)
      .forEach((prop) => {
        if (obj[prop] instanceof Timestamp) {
          newObj[prop] = obj[prop].toDate();
        } else {
          this.convertObjectTimestampPropertiesToDate(obj[prop]);
        }
      });

    return {
      ...obj,
      ...newObj,
    };
  } // convertObjectTimestampPropertiesToDate()

  /**
   * @param {any} context
   * @param {string} param
   * @param {Array<Array<string>> | null} [constraints]
   * @param {string | null} [queryOrderBy]
   * @param {'asc' | 'desc' | null } [orderDirection]
   * @param {number | null} [queryLimit]
   */
  async bindAll(
    context,
    param,
    constraints = null,
    queryOrderBy = null,
    orderDirection = null,
    queryLimit = null,
  ) {
    if (network.offlineMode) {
      const genericIndexedDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIndexedDb.bindAll(context, param, constraints, queryOrderBy, orderDirection, queryLimit);
    }

    const ref = this.getQuery(constraints, queryOrderBy, orderDirection, queryLimit);

    try {
      return context.$firestoreBind(param, ref);
    } catch (err) {
      this.#handleDbError(err.message, 'executing', {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
        collectionPath: this.errorCollectionPath,
      });
    }
  } // bindAll()

  // @todo: refactor into static to fix eslint error
  unbind(context, param) {
    if (network.offlineMode) {
      const genericIndexedDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIndexedDb.unbind(context, param);
    }

    return context.$firestoreUnbind(param);
  } // unbind()

  /**
   * @param {any} context
   * @param {string} param
   * @param {string} docId
   */
  bindDocument(context, param, docId) {
    if (network.offlineMode) {
      const genericIndexedDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIndexedDb.bindDocument(context, param, docId);
    }
    try {
      return context.$firestoreBind(param, this.getDocRef(docId));
    } catch (err) {
      this.#handleDbError(err.message, 'executing', {
        documentId: docId,
        collectionPath: this.errorCollectionPath,
      });
    }
  }

  /**
   * Generate a random document id
   * @returns {string} - returns a new doc id
   */
  getDocId() {
    const collectionRef = this.getCollectionRef();
    return doc(collectionRef).id;
  }

  /**
   * @param {Array<Array<string>>} constraints
   * @param {string} queryOrderBy
   * @param {'asc' | 'desc' } orderDirection
   * @param {number} queryLimit
   * @param {number | null} [offset]
   */
  async readPaginated(constraints, queryOrderBy, orderDirection, queryLimit, offset = null) {
    if (network.offlineMode) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIdxDb.readPaginated(constraints, queryOrderBy, orderDirection, queryLimit, offset);
    }

    let queryBuilder;
    const collectionRef = this.getCollectionRef();

    const whereQueries = map(constraints, (constraint) => where(...constraint));
    queryBuilder = query(collectionRef, ...whereQueries);
    queryBuilder = query(queryBuilder, orderBy(queryOrderBy, orderDirection));

    if (offset !== null) {
      queryBuilder = query(queryBuilder, startAfter(offset));
    }

    queryBuilder = query(queryBuilder, limit(queryLimit));

    return getDocs(queryBuilder);
  } // readPaginated()

  // eslint-disable-next-line class-methods-use-this
  async countRecords(countQuery) {
    if (!network.offlineMode) {
      const countSnapshot = await getCountFromServer(countQuery);
      return countSnapshot.data().count;
    }

    return null;
  }

  /**
   * Added this method to replace the call of the Firebase's `runTransaction` function in offline mode so it
   * interacts only with IndexedDB. This is a temporary decision and we still need to implement `GenericIndexDB`'s
   * transaction functionality.
   *
   * todo: Create a separate transaction method for `GenericIndexDB`
   *
   * @param _
   * @param {(transaction) => Promise<void>} callback
   * @return {Promise<any>}
   */
  // eslint-disable-next-line class-methods-use-this
  async offlineTransaction(_, callback) {
    const transaction = {};
    return callback(transaction);
  }

  /**
   * @param {(TransactionMethods) => Promise<void>} callback
   * @return {Promise<*>}
   *
   * TODO For now transaction methods don't return anything in offline mode
   */
  async executeTransaction(callback) {
    if (this.constructor !== GenericDB) {
      throw new Error('executeTransaction() should be called on GenericDB instance');
    }

    const indexDBActions = [];

    const transactionExecutor = network.offlineMode ? this.offlineTransaction : runTransaction;

    const result = await transactionExecutor(db, async (transaction) => {
      const transactionMethods = {
        read: (collDb, readArgs) => collDb.#readWithTransaction(transaction, readArgs),
        readAll: (collDb, readAllArgs = {}) => collDb.#readAllWithTransaction(transaction, readAllArgs),
        create: async (collDb, createArgs) => {
          const createdDocument = await collDb.#createWithTransaction(transaction, createArgs);
          indexDBActions.push(() => collDb.#createDataInIdxDb(
            createArgs.data,
            createdDocument?.id || createArgs?.id || null,
            createArgs.merge || false,
            createArgs.overwriteCreateTimestamp || null,
            createArgs.overwriteUpdateTimestamp || null,
          ));
          return createdDocument;
        },
        update: (collDb, updateArgs) => {
          const { data } = updateArgs;
          indexDBActions.push(() => collDb.#updateDataInIdxDB(data));
          return collDb.#updateWithTransaction(transaction, updateArgs);
        },
        set: (collDb, setArgs) => {
          const { data, id } = setArgs;
          indexDBActions.push(() => collDb.#setDataToIdxDB(data, id));
          return collDb.#setWithTransaction(transaction, setArgs);
        },
        delete: (collDb, id) => {
          indexDBActions.push(() => collDb.#deleteDataInIdxDB(id));
          return collDb.#deleteWithTransaction(transaction, id);
        },
      };

      return callback(transactionMethods);
    });

    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const action of indexDBActions) {
        // eslint-disable-next-line no-await-in-loop
        await action();
      }
    } catch (err) {
      console.error('Error after transaction: failed to store data in indexedDB', err);
    }

    return result;
  }

  /**
   * Read a document in the collection
   * @param {Transaction} transaction
   * @param {string} id
   * @param {boolean} [forceIndexedDB]
   */
  async #readWithTransaction(transaction, { id, forceIndexedDB = false } = {}) {
    if (network.offlineMode || forceIndexedDB) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIdxDb.read(id);
    }

    let result = null;
    try {
      const docRef = this.getDocRef(id);
      result = await transaction.get(docRef);
    } catch (err) {
      this.#handleDbError(err, 'execute-transaction', {
        documentId: id,
        collectionPath: this.errorCollectionPath,
        transactionMethod: 'read',
      });
    }

    const data = result && result.exists() ? result.data() : null;
    if (isNil(data)) {
      return null;
    }

    return { id, ...data };
  }

  /**
   * Read all documents in the collection following constraints
   *
   * @param {Transaction} transaction
   * @param {ReadAllArgs} readAllArgs
   * @return {Promise<Array<{id: string} & Object>>}
   */
  async #readAllWithTransaction(transaction, {
    constraints = null,
    queryOrderBy = null,
    orderDirection = null,
    queryLimit = null,
    offset = null,
    forceIndexedDB = false,
  } = {}) {
    if (network.offlineMode || forceIndexedDB === true) {
      const genericIdxDb = new GenericIndexedDb(this.getCollectionRef().path);
      return genericIdxDb.readAll(constraints, queryOrderBy, orderDirection, queryLimit, offset);
    }

    let queryBuilder = this.getCollectionRef();

    if (constraints && isArray(constraints) && constraints.length >= 1) {
      const whereConstraints = await this.#handleConstraints(constraints, transaction);
      if (this.#isDocData(head(whereConstraints))) {
        return whereConstraints;
      }

      queryBuilder = query(queryBuilder, ...whereConstraints);
    }

    queryBuilder = this.#manageQuerySettings(queryBuilder, queryOrderBy, orderDirection, queryLimit, offset);

    try {
      /** There is no transaction method to fetch all documents using some query so we are using getDocs and iterate
       * over results using transaction get method */
      const docsRef = await getDocs(queryBuilder);
      const result = docsRef.docs.map((documentSnapshot) => transaction.get(documentSnapshot.ref));
      const data = await Promise.all(result);
      return this.#formatDocsRef({ docs: data });
    } catch (err) {
      this.#handleDbError(err, 'execute-transaction', {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
        offset,
        collectionPath: this.errorCollectionPath,
        transactionMethod: 'readAll',
      });
    }
  }

  /**
   * @param {Transaction} transaction
   * @param {CreateArgs} CreateArgs
   * @return {Promise<void>}
   */
  async #createWithTransaction(transaction, {
    data,
    id = null,
    merge = false,
    overwriteCreateTimestamp = null,
    overwriteUpdateTimestamp = null,
  }) {
    if (network.offlineMode) {
      return;
    }

    const collectionRef = this.getCollectionRef();

    const timestamp = serverTimestamp();
    const dataToCreate = {
      ...data,
      createTimestamp: overwriteCreateTimestamp || timestamp,
      updateTimestamp: overwriteUpdateTimestamp || timestamp,
      updateBy: get(auth, ['currentUser', 'uid']) || null,
    };
    // The id field is reserved and should not be included
    delete dataToCreate.id;

    let docId = null;

    try {
      if (isNil(id)) {
        const docRef = await doc(collectionRef);
        await transaction.set(docRef, dataToCreate);
        docId = docRef.id;
      } else {
        const docRef = doc(collectionRef, id);
        await transaction.set(docRef, dataToCreate, { merge });
        docId = id;
      }
    } catch (err) {
      this.#handleDbError(err, 'execute-transaction', {
        dataToCreate,
        id,
        merge,
        collectionPath: this.errorCollectionPath,
        transactionMethod: 'create',
      });
    }

    return {
      ...data,
      id: docId,
      createTimestamp: overwriteCreateTimestamp instanceof Timestamp ? overwriteCreateTimestamp.toDate() : new Date(),
      updateTimestamp: overwriteUpdateTimestamp instanceof Timestamp ? overwriteUpdateTimestamp.toDate() : new Date(),
      updateBy: get(auth, ['currentUser', 'uid']) || null,
    };
  }

  /**
   * Update a document in the collection
   * @param {Transaction} transaction
   * @param {{id: string} & Object} data
   * @param {boolean} [forceIndexedDB]
   */
  async #updateWithTransaction(transaction, {
    data,
    forceIndexedDB = false,
  }) {
    if (network.offlineMode || forceIndexedDB === true) {
      return;
    }

    const { id } = data;

    try {
      const docRef = this.getDocRef(id);
      await transaction.update(docRef, {
        ...omit(data, 'id'),
        updateTimestamp: serverTimestamp(),
        updateBy: get(auth, ['currentUser', 'uid']) || null,
      });
    } catch (err) {
      this.#handleDbError(err, 'execute-transaction', {
        data,
        id,
        collectionPath: this.errorCollectionPath,
        transactionMethod: 'update',
      });
    }

    return id;
  }

  /**
   * Set specific data on a document in the collection
   * @param {Transaction} transaction
   * @param {Object} data Data to set
   * @param {string | null} [id] Document id to set data on
   * @param {boolean} [forceIndexedDB] - force to set data to indexedDB
   * @return Promise<void>
   */
  async #setWithTransaction(transaction, {
    data,
    id = null,
    forceIndexedDB = false,
  } = {}) {
    if (!id) {
      throw new Error(`Document id is missing (${this.errorCollectionPath} collection)`);
    }

    if (network.offlineMode || forceIndexedDB) {
      return;
    }

    try {
      const docRef = this.getDocRef(id);
      const docData = {
        ...data,
        updateTimestamp: serverTimestamp(),
        updateBy: get(auth, ['currentUser', 'uid']) || null,
      };

      transaction.set(docRef, docData, { merge: true });
    } catch (err) {
      this.#handleDbError(err, 'execute-transaction', {
        data,
        id,
        collectionPath: this.errorCollectionPath,
        transactionMethod: 'set',
      });
    }
  }

  /**
   * Delete a document in the collection
   * @param {Transaction} transaction
   * @param id
   */
  async #deleteWithTransaction(transaction, id) {
    if (network.offlineMode) {
      return;
    }

    try {
      const docRef = this.getDocRef(id);
      await transaction.delete(docRef);
    } catch (err) {
      this.#handleDbError(err, 'execute-transaction', {
        documentId: id,
        collectionPath: this.errorCollectionPath,
        transactionMethod: 'delete',
      });
    }

    try {
      const deletedDataCollectionRef = collection(db, 'deletedData');
      const expiresAt = Timestamp.fromMillis(Date.now() + (14 * 24 * 60 * 60 * 1000));
      await transaction.set(doc(deletedDataCollectionRef), {
        id,
        collectionName: this.getCollectionRef().path,
        clinic: currentClinic.id,
        deleteTimestamp: serverTimestamp(),
        expiresAt,
      });
    } catch (err) {
      // We don't need to throw IDB error, even if there is one
    }
  }

  /**
   * @param {string} message
   * @param {DbOperation} operationName
   * @param {Object} logData
   */
  #handleDbError(message, operationName, logData) {
    let operationSubject = '';
    switch (operationName) {
      case 'executing':
        operationSubject = 'query';
        break;
      case 'execute-transaction':
        operationSubject = 'transaction';
        break;
      default:
        operationSubject = 'document';
        break;
    }
    const errMessage = `Error ${operationName} a "${this.errorCollectionPath}" ${operationSubject}: ${message}`;

    console.error(errMessage);
    logEvent(errMessage, {
      context: {
        context_data: {
          logData,
        },
      },
    });
    throw new Error(errMessage);
  }
}
